import { useEffect, useState } from "react";
import { LegalLayout, Clause } from "./LegalLayout";
import { ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * The public non-affiliation disclaimer, linked from the footer of every page
 * on the marketing site and inside the signed-in app. Text and sources come
 * from the backend so this page can never drift from the PDFs or the app.
 */
export default function LegalDisclaimer() {
  const [d, setD] = useState(null);

  useEffect(() => {
    fetch(`${API}/compliance/disclosures`).then((r) => r.json()).then(setD).catch(() => setD(null));
  }, []);

  return (
    <LegalLayout eyebrow="LEGAL · DISCLAIMER" title="Disclaimer &amp; Non-Affiliation"
                 updated="2026-06-01" active="disclaimer">
      <Clause n="1" heading="We are not a government entity">
        <p className="border-2 border-ink bg-ink text-white p-4 font-black leading-relaxed"
           data-testid="disclaimer-statement">
          {d?.disclaimer
            || "Checkmate Payroll is a private software provider. We are not affiliated with, endorsed by, or representing any government entity. All tax and payroll logic is based on publicly available IRS and state documentation."}
        </p>
        <p>
          Checkmate Payroll is a private company selling software: compliance-ready payroll tools for
          US businesses. We use no government logo, seal, name or imagery to imply otherwise, and we are
          not endorsed by, contracted to, or acting on behalf of the IRS, the Social Security
          Administration, any state tax or labor agency, or any other public body.
        </p>
      </Clause>

      <Clause n="2" heading="What 'compliance-ready' means, and what it does not">
        <p>
          Checkmate <b>prepares</b> payroll figures, documents and agency-format electronic files from the
          data you enter, using the published tables and specifications listed below. <b>You submit them.</b>{" "}
          The W-2 wage file goes to ssa.gov/bso, 1099 filings to irs.gov/iris, state wage reports to your
          state agency's portal — and you keep the agency's confirmation.
        </p>
        <p>
          Generating a document in this product is <b>not</b> the same as an agency receiving it, accepting
          it, or agreeing with it. Nothing in Checkmate is a filing service, a guarantee of compliance, or a
          substitute for professional judgement. This is not tax, legal or accounting advice — consult a
          licensed CPA.
        </p>
      </Clause>

      <Clause n="3" heading="Where our numbers come from">
        <p>
          Every calculation traces to a public document. These are the actual inputs to the engine, not a
          reading list — each drives the calculation named beside it.
        </p>
        <ul className="space-y-3 mt-3" data-testid="disclaimer-sources">
          {(d?.sources || []).map((s) => (
            <li key={s.url} className="border-l-2 border-ink pl-3">
              <a href={s.url} target="_blank" rel="noreferrer"
                 className="font-black underline inline-flex items-start gap-1 hover:text-accent">
                {s.name} <ExternalLink className="w-3 h-3 mt-0.5 shrink-0" />
              </a>
              <div className="text-[11px] text-muted-foreground mt-0.5">{s.used_for}</div>
              <div className="text-[11px] text-muted-foreground break-all">{s.url}</div>
            </li>
          ))}
        </ul>
      </Clause>

      <Clause n="4" heading="The same statement travels with your documents">
        <p>
          This disclaimer is printed in the footer of every PDF Checkmate generates — pay stubs, worksheets,
          the year-end checklist, the quarterly tax summary and the Director's Handbook — so the
          non-affiliation notice stays attached to physical deliverables that leave the app. We deliberately
          do <b>not</b> print it inside official IRS or SSA form templates, because altering a
          machine-scannable government form can invalidate it.
        </p>
        <p>
          It is also served at{" "}
          <a href={`${API}/compliance/disclosures`} target="_blank" rel="noreferrer" className="underline">
            /api/compliance/disclosures
          </a>{" "}
          with no account required, and shown in the app under Company → Compliance &amp; Disclaimers.
        </p>
      </Clause>
    </LegalLayout>
  );
}
